import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectCurrentInbox,
  selectCurrentBillingCycle,
  selectCurrentKb,
  selectPlans,
  selectIsSubscriptionActive,
  selectUpdateSubscriptionRequest,
  selectIsTrial,
  selectPrimaryCreditCard,
  selectIsBillingEstimateAvailable,
  selectBillingUsage,
  selectShowCardErrorModal,
  selectIsUpdatingCard,
  selectIsCanceling,
  selectHasPlans,
  selectIsBillingDataLoaded,
  selectIsBillingDataLoading,
  selectBillingPendingSubscription,
  selectBillingProvider,
  selectBillingProviderSettings,
  selectBillingExternalAccountId,
  selectBillingExternalSubscriptionId,
  selectHasCompletedTrial,
  selectBillingStartTrialDurationDays,
  selectDaysLeftBeforeExpiring,
} from 'ducks/billing/selectors'
import {
  doUpdateCard,
  doUpdateSubscription,
  doFetchBillingData,
  doExternalApprovePendingSubscription,
  doStartTrial,
} from 'ducks/billing/operations'
import {
  buildLineItemsFromPlans,
  calculateSubscriptionFlatTotal,
  calculateSubscriptionUsageTotal,
} from './util'

export const useBilling = () => {
  const dispatch = useDispatch()
  const provider = useSelector(selectBillingProvider)
  const billingCycle = useSelector(selectCurrentBillingCycle)
  const currentInbox = useSelector(selectCurrentInbox)
  const currentKb = useSelector(selectCurrentKb)
  const plans = useSelector(selectPlans)
  const isSubscriptionActive = useSelector(selectIsSubscriptionActive)
  const { loading: isSaving } = useSelector(selectUpdateSubscriptionRequest)
  const isTrial = useSelector(state => selectIsTrial(state, 'inbox'))
  const primaryCard = useSelector(selectPrimaryCreditCard)
  const isBillingEstimateAvailable = useSelector(
    selectIsBillingEstimateAvailable
  )
  const usage = useSelector(selectBillingUsage)
  const showCardError = useSelector(selectShowCardErrorModal)
  const isUpdatingCard = useSelector(selectIsUpdatingCard)
  const isCanceling = useSelector(selectIsCanceling)
  const hasPlans = useSelector(selectHasPlans)
  const isLoaded = useSelector(selectIsBillingDataLoaded)
  const isLoading = useSelector(selectIsBillingDataLoading)
  const pendingSubscription = useSelector(selectBillingPendingSubscription)
  const providerSettings = useSelector(selectBillingProviderSettings)
  const isBillingLinked = !!useSelector(selectBillingExternalAccountId)
  const externalAccountId = useSelector(selectBillingExternalAccountId)
  const externalSubscriptionId = useSelector(
    selectBillingExternalSubscriptionId
  )
  const hasCompletedTrial = useSelector(selectHasCompletedTrial)
  const startTrialDurationDays = useSelector(
    selectBillingStartTrialDurationDays
  )

  const trialDaysLeft = useSelector(state =>
    selectDaysLeftBeforeExpiring(state, 'inbox')
  )

  const updateCard = useCallback(
    stripeToken => dispatch(doUpdateCard(stripeToken)),
    [dispatch]
  )

  const updateSubscription = useCallback(
    (form, options) => {
      const { inboxPlanId, kbPlanId, billingCycle: formBillingCycle } = form
      return dispatch(
        doUpdateSubscription(
          {
            inbox: inboxPlanId,
            kb: kbPlanId,
            cycle: formBillingCycle,
          },
          options
        )
      )
    },
    [dispatch]
  )

  const startTrial = useCallback(
    () => {
      return dispatch(doStartTrial('inbox'))
    },
    [dispatch]
  )

  const reloadBilling = useCallback(
    () => {
      return dispatch(doFetchBillingData())
    },
    [dispatch]
  )

  const submitForm = useCallback(formId => {
    document
      .getElementById(formId)
      .dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))
  }, [])

  const externalApprovePendingSubscription = useCallback(
    options => dispatch(doExternalApprovePendingSubscription(options)),
    [dispatch]
  )

  const updateCardAndStartSubscription = useCallback(
    async (stripeToken, formId) => {
      await updateCard(stripeToken)
      submitForm(formId)
    },
    [updateCard, submitForm]
  )

  const buildLineItemsFromPlansForBillingCycle = useCallback(
    forBillingCycle =>
      buildLineItemsFromPlans({
        inboxPlanId: currentInbox?.plan?.planId,
        kbPlanId: currentKb?.plan?.planId,
        billingCycle: forBillingCycle,
        plans,
        usage,
        provider,
      }),
    [
      currentInbox?.plan?.planId,
      currentKb?.plan?.planId,
      plans,
      provider,
      usage,
    ]
  )

  const calculateEstimatedTotalBill = useCallback(
    forBillingCycle => {
      const lineItems = buildLineItemsFromPlansForBillingCycle(forBillingCycle)
      const flatTotal = calculateSubscriptionFlatTotal(lineItems) || 0
      const usageTotal = calculateSubscriptionUsageTotal(lineItems) || 0

      return flatTotal + usageTotal
    },
    [buildLineItemsFromPlansForBillingCycle]
  )

  return {
    billingCycle,
    currentInbox,
    currentKb,
    externalAccountId,
    externalApprovePendingSubscription,
    externalSubscriptionId,
    hasCompletedTrial,
    hasPlans,
    isBillingEstimateAvailable,
    isBillingLinked,
    isCanceling,
    isLoaded,
    isLoading,
    isSaving,
    isSubscriptionActive,
    isTrial,
    isUpdatingCard,
    pendingSubscription,
    plans,
    primaryCard,
    provider,
    providerSettings,
    reloadBilling,
    showCardError,
    startTrial,
    startTrialDurationDays,
    submitForm,
    trialDaysLeft,
    updateCard,
    updateCardAndStartSubscription,
    updateSubscription,
    usage,
    calculateEstimatedTotalBill,
  }
}
